/**
 * Kişi (v0.1.64): what one member of the team did. Sercan: "bir kişiye tıklayınca
 * yaptıklarını görelim; verdiği onaylar, hazırladığı mailler, yaptığı aramalar". A header
 * with the counters of the window, the tools an agent called, and a timeline of runs,
 * proposals, decisions, questions, sends and scans, newest first, each linking to its card.
 * Read only.
 */

import { type ReactNode, useEffect } from 'react';
import {
  PERSON_COUNTER_LABELS,
  PERSON_EVENT_LABELS,
  type PersonActivity,
  type PersonEvent,
  toolLabel,
} from '../api/people.js';
import { useIsDesktop } from '../app/media.js';
import { usePerson } from '../app/people.js';
import { Link } from '../app/router.js';
import { Icon } from '../components/icons.js';
import { SectionHeader } from '../components/primitives.js';
import { AppBar, BottomTabBar, StatusArea, TopBar, TopBarStatus } from '../components/shell.js';
import { formatClock, formatDayMonth, formatUsd } from '../lib/format.js';
import { Loading } from './common.js';

function EventRow({ event }: { event: PersonEvent }) {
  const body = (
    <>
      <span className="customers-event__title">
        {event.title}
        {event.costMicroUsd !== null && event.costMicroUsd > 0 ? (
          <span className="mono customers-event__amount"> {formatUsd(event.costMicroUsd, 'auto')}</span>
        ) : null}
      </span>
      <span className="kisi-event__meta">
        {PERSON_EVENT_LABELS[event.kind] ?? event.kind}
        {event.status ? ` · ${event.status}` : ''}
      </span>
      {event.detail ? <span className="customers-event__text">{event.detail}</span> : null}
    </>
  );
  return (
    <li className={`customers-event kisi-event kisi-event--${event.kind}`}>
      <span className="customers-event__when mono">
        {formatDayMonth(event.at)} {formatClock(event.at)}
      </span>
      {event.link ? (
        <Link to={event.link} className="customers-event__body kisi-event__link">
          {body}
        </Link>
      ) : (
        <span className="customers-event__body">{body}</span>
      )}
    </li>
  );
}

function PersonBody({ view }: { view: PersonActivity }) {
  const counters = PERSON_COUNTER_LABELS.filter(({ key }) => view.counters[key] > 0);
  return (
    <>
      <section aria-labelledby="kisi-ozet" className="section section--tight">
        <SectionHeader
          id="kisi-ozet"
          title={`Son ${view.days} gün`}
          right={
            view.counters.costMicroUsd > 0 ? (
              <span className="section-header__aside">
                maliyet <span className="mono">{formatUsd(view.counters.costMicroUsd, 'auto')}</span>
              </span>
            ) : undefined
          }
        />
        {counters.length === 0 ? (
          <p className="shop-hint">Bu pencerede kayıtlı bir iş yok.</p>
        ) : (
          <ul className="kisi-counters" aria-label="Sayılar">
            {counters.map(({ key, label }) => (
              <li key={key} className="kisi-counter">
                <span className="mono kisi-counter__n">{view.counters[key]}</span> {label}
              </li>
            ))}
          </ul>
        )}
      </section>

      {view.tools.length > 0 ? (
        <section aria-labelledby="kisi-araclar" className="section">
          <SectionHeader id="kisi-araclar" title="Kullandığı araçlar" count={view.tools.length} />
          <ul className="kisi-tools" aria-label="Araç çağrıları">
            {view.tools.map((tool) => (
              <li key={tool.tool} className="kisi-tool">
                <span className="kisi-tool__name">{toolLabel(tool.tool)}</span>
                <span className="mono">
                  {tool.calls}
                  {tool.denied > 0 ? <span className="kisi-tool__denied"> · {tool.denied} reddedildi</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section aria-labelledby="kisi-zaman" className="section">
        <SectionHeader id="kisi-zaman" title="Yaptıkları" count={view.timeline.length} />
        {view.timeline.length === 0 ? (
          <p className="shop-hint">Henüz bir kayıt yok.</p>
        ) : (
          <ol className="customers-timeline kisi-timeline" aria-label={`${view.person.name} yaptıkları`}>
            {view.timeline.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </ol>
        )}
      </section>
    </>
  );
}

export function Kisi({ id }: { id: string }) {
  const desktop = useIsDesktop();
  const { store, state } = usePerson(id);
  useEffect(() => {
    void store.ensureLoaded(id);
  }, [store, id]);
  const view = state.view;
  const title = view?.person.name ?? 'Kişi';
  const subtitle = view === null ? '' : `${view.person.kind === 'agent' ? 'Ajan' : 'Kişi'} · ${view.person.role}`;
  let body: ReactNode;
  if (view !== null) {
    body = <PersonBody view={view} />;
  } else if (state.error !== null) {
    body = (
      <div className="shop-status" role="status">
        Kişi yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={() => void store.reload(id)}>
          Tekrar dene
        </button>
      </div>
    );
  } else {
    body = <Loading />;
  }
  if (desktop) {
    return (
      <>
        <TopBar title={title} status={<TopBarStatus extra={subtitle} />} />
        <main className="desk-main desk-main--narrow">
          <Link to="/ekip" className="back-link">
            <Icon name="chevron-left" size={18} />
            Ekip
          </Link>
          {body}
        </main>
      </>
    );
  }
  return (
    <>
      <AppBar title={title} subtitle={subtitle} />
      <StatusArea />
      <main className="phone-main">
        <Link to="/ekip" className="back-link">
          <Icon name="chevron-left" size={18} />
          Ekip
        </Link>
        {body}
      </main>
      <BottomTabBar active="ekip" />
    </>
  );
}
